<template>
  <div class="container section-p70">
    <div class="row">
      <div class="col-auto">
        <title-with-results :title="t('SEARCH_QUERY')" :number-of-results="paginatedTableRef?.numberOfResults?.()"/>
      </div>
    </div>

    <div class="row mt-3 justify-content-between align-items-center">
      <form class="col" @submit.prevent="onSearch">
        <div class="row align-items-center">
          <div class="col-xl-4 mb-2" v-if="roleSearchOptions.length > 1">
            <MultiSelect
                v-model="searchSelectedRoles"
                :options="roleSearchOptions"
                optionLabel="label"
                optionValue="value"
                :placeholder="t('ROLE')"
                :showToggleAll="false"
                filter
                class="w-100"
            />
          </div>

          <div class="col-xl-4 mb-2">
            <InputText type="text" v-model="searchValue" :placeholder="t('SEARCH')" class="w-100"/>
          </div>

          <div class="col-xl-4 mb-2">
            <Button type="submit" :label="t('SEARCH')"/>
          </div>
        </div>
      </form>

      <div class="col flex-grow-0">
        <Button :label="t('ACTIONS')" @click="actionsMenuRef?.toggle($event)" iconPos="right" icon="pi pi-chevron-down"/>
        <Menu ref="actionsMenuRef" :model="actionsMenuItems" :popup="true"/>
      </div>
    </div>

    <div class="row">
      <div class="col">
        <PaginatedTable id="paginated-accounts"
                        :fields="fields"
                        :on-page-change="search"
                        :values-to-save="{searchValue: searchValue, roles: searchSelectedRoles}"
                        ref="paginatedTableRef"
        >
          <template v-slot:cell(name)="{ item }">
            <span class="me-2">
              {{ item.fullName || "-" }}
            </span>

            <Badge v-if="item.isArchived" severity="warn" class="me-2" size="large">{{ t("ARCHIVED") }}</Badge>
            <Badge v-if="item.activeTill" severity="warn" class="me-2" size="large">{{ t("ACTIVE_TILL") }}: {{ dateFormat(item.activeTill) }}</Badge>
            <Badge v-if="!item.isActivated" severity="info" class="me-2" size="large">{{ t("NOT_ACTIVATED") }}</Badge>
          </template>
          <template v-slot:cell(ssn)="{ item }">
            <router-link :to="{ name: 'Trainee', params: { id: item.id } }">
              {{ item.ssn }}
            </router-link>
          </template>
          <template v-slot:cell(view)="{ item }">
            <div>
              <router-link :to="{ name: 'Trainee', params: { id: `${item.id}`, isArchived: item.isArchived } }">
                <Button
                    :label="t('TRAINING_CARD')"
                    :disabled="item.isArchived === true && !auth.canSeeArchivedAccounts()"
                />
              </router-link>
            </div>
          </template>
          <template v-slot:cell(see)="{ item }">
            <div v-if="item.role === Role.TRAINEE">
              <router-link :to="{ name: 'Groups', params: { studentId: `${item.id}` } }">
                <Button icon="pi pi-eye" text rounded/>
              </router-link>
            </div>
          </template>
          <template v-slot:cell(role)="{ item, index }">
            <div class="d-flex align-items-center role-cell" v-if="auth.hasRole(Role.ADMIN) && auth.getAcc() !== item.id">
              <Select
                  v-model="item.role"
                  :options="roleOptions"
                  option-label="text"
                  option-value="value"
                  class="role-select"
              />
              <Button
                  :label="t('SAVE')"
                  class="ms-2"
                  @click="changeRole(item.role, item.ssn)"
                  v-if="paginatedTableRef?.valueChanged(item, 'role', index)"
              />
            </div>
            <span v-else>{{ t(item.role) }}</span>
          </template>
        </PaginatedTable>
      </div>
    </div>
  </div>

  <AddAccountModal id="add-account-modal"
                   ref="addAccountModalRef"
                   :after-save="resetSearchResults"/>

  <file-downloader ref="fileDownloaderRef"/>
</template>

<script setup>
import {computed, nextTick, ref} from "vue";
import {injectAxios, injectAuth} from "@/util/inject";
import {useRoute} from "vue-router";
import {useI18n} from "vue-i18n";
import {Role, Role as $roles} from "@/auth/oauth";
import PaginatedTable from "@/views/components/PaginatedTable.vue";
import AddAccountModal from "@/views/components/modals/AddAccountModal.vue";
import {useDateFormat} from "@/mixins/dateFormatMixin2";
import {useToast} from "@/util/toastUtils";
import FileDownloader from "@/components/FileDownloader.vue";
import TitleWithResults from "@/components/TitleWithResults.vue";

const route = useRoute();
const {t} = useI18n();
const toast = useToast();
const auth = injectAuth();
const axios = injectAxios();
const {dateFormat} = useDateFormat();

const roleOptions = computed(() => {
  return [
    {value: $roles.TRAINEE, text: t("TRAINEE")},
    {value: $roles.TRAINER, text: t("TRAINER")},
    {value: $roles.ADMIN, text: t("ADMIN")},
    {value: $roles.CONTENT_MANAGER, text: t("CONTENT_MANAGER")},
    {value: $roles.SCHOOL_MANAGER, text: t("SCHOOL_MANAGER")},
    {value: $roles.VIEWER, text: t("VIEWER")},
    {value: $roles.DISABLED_USER, text: t("DISABLED_USER")},
  ];
});

const roleSearchOptions = computed(() => {
  let result = [];

  if (auth.hasRole(Role.ADMIN)) {
    result = [Role.TRAINEE, Role.TRAINER, Role.ADMIN, Role.SCHOOL_MANAGER, Role.CONTENT_MANAGER, Role.VIEWER, Role.DISABLED_USER];
  } else if (auth.hasRole(Role.VIEWER)) {
    result = [Role.TRAINEE, Role.TRAINER];
  } else if (auth.hasSomeRole(Role.TRAINER, Role.SCHOOL_MANAGER)) {
    result = [Role.TRAINEE];
  }

  return result.map((r) => ({label: t(r), value: r}));
});

const rolesAsList = (roles) => {
  if (!roles) return [];
  return (typeof roles == "string")
      ? [roles]
      : roles;
}

const paginatedTableRef = ref(null);
const addAccountModalRef = ref(null);
const fileDownloaderRef = ref(null);
const actionsMenuRef = ref(null);

const actionsMenuItems = computed(() => {
  const items = [];

  if (auth.hasRole(Role.ADMIN)) {
    items.push({label: t("REGISTER_NEW_USER"), command: openAddAccountModal});
  }

  items.push({label: `${t("DOWNLOAD_FILE")} (Excel)`, command: downloadAccountsExcel});
  return items;
});

const searchSelectedRoles = ref(rolesAsList(route.query.roles));
const searchValue = ref(route.query.searchValue?.toString());

const changeRole = async (role, ssn) => {
  const resp = await axios.patch("/account/role", {role: role, ssn: ssn});
  await paginatedTableRef.value?.fetchRows();
  toast.makeSuccessToast(`${t("SAVED")} - ${t(resp.data.role)}`);
};

const onSearch = async (ev) => {
  ev.preventDefault();
  await paginatedTableRef.value.onSearch();
};

const resetSearchResults = async () => {
  searchValue.value = null;
  searchSelectedRoles.value = [];
  await paginatedTableRef.value.reset();
};

const getSearchParams = () => {
  return {
    "searchValue": searchValue.value,
    "roles": (searchSelectedRoles.value.length ? searchSelectedRoles.value : roleSearchOptions.value.map(role => role.value)).join(","),
    "fields": "SSN,NAME,EMAIL,PHONE",
  };
};

const search = async (params) => {
  const resp = await axios.get("/account/search/all", {params: {...params, ...getSearchParams()}});
  await nextTick();
  return resp;
};

const downloadAccountsExcel = async () => {
  await fileDownloaderRef.value.downloadFile("/account/search/all/excel", getSearchParams(), "accounts.xlsx");
  await paginatedTableRef.value.onSearch();
};

const fields = computed(() => {
  return [
    {key: "name", label: t("NAME"), sortable: true},
    {key: "ssn", label: t("SSN"), sortable: true},
    {key: "email", label: t("EMAIL"), sortable: true},
    {key: "phone", label: t("PHONE"), sortable: true},
    {key: "role", label: t("ROLE"), sortable: false},
    {key: "see", label: "", sortable: false},
    {key: "view", label: "", sortable: false},
  ];
});

const openAddAccountModal = () => {
  addAccountModalRef.value.openModal();
};
</script>

<style scoped>
.role-cell > * {
  width: 100%;
}
</style>
